import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Box, Card, Typography, AppBar, Toolbar, IconButton,
    Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
    Divider, Alert, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SyncIcon from '@mui/icons-material/Sync';
import ClipLoader from 'react-spinners/ClipLoader';
import SchedulerLab from '../scheduleLab/SchedulerLab';
import ReqLabConfirm from '../reqLab/ReqLabConfirm';
import LabConsolleImportFromEasy from './LabConsolleImportFromEasy';
import LabConsolleModifyAF from './LabConsolleModifyAF';

const themeColor = '#a71e3b';

// Normalizza adSelList: gestisce retrocompatibilità con vecchio formato adSel
function normalizeAdSelList(labProps) {
    // Priorità: adSelList dal backend (nuovo formato)
    if (labProps?.adSelList && Array.isArray(labProps.adSelList) && labProps.adSelList.length > 0) {
        return labProps.adSelList;
    }
    // Retrocompatibilità: adSel singolo (vecchio formato)
    if (labProps?.adSel && labProps.adSel.anaModCod) {
        return [labProps.adSel];
    }
    return [];
}

function LabConsolleScheduleDettPage(props) {
    const [isSubmitted, setIsSubmitted] = useState(0);
    const [labEvents, setLabEvents] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitble, setIsSubmitble] = useState(false);
    const [isLoading, setIsLoading] = useState(0);
    const [statusRequest, setStatusRequest] = useState();
    const [adSelList, setAdSelList] = useState(() => normalizeAdSelList(props.labProps));
    const [showDisassociaDialog, setShowDisassociaDialog] = useState(false);
    const [disassociaTarget, setDisassociaTarget] = useState(null); // null = tutte, index = singola
    const [isDisassociating, setIsDisassociating] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [lastAddedAd, setLastAddedAd] = useState(null);
    const importRef = useRef(null);
    const adSelListRef = useRef(adSelList);

    // Mantieni la ref sincronizzata con lo stato
    useEffect(() => {
        adSelListRef.current = adSelList;
    }, [adSelList]);

    const {
        labProps,
        isAdmin,
    } = props;

    const hasAdSel = adSelList.length > 0;

    // Carica adSelList e eventi dal backend al mount
    useEffect(() => {
        const loadData = async () => {
            if (!labProps?.idLab) return;
            try {
                const response = await fetch(
                    process.env.REACT_APP_URL_GET_USAGE_DATA + '?idLab=' + labProps.idLab,
                    { credentials: 'include', method: "GET" }
                );
                if (response.ok) {
                    const data = await response.json();
                    // Carica eventi
                    if (data.schedulerLab) {
                        setLabEvents(data.schedulerLab);
                    }
                    // Carica adSelList dal backend (nuovo formato)
                    if (data.adSelList && Array.isArray(data.adSelList) && data.adSelList.length > 0) {
                        setAdSelList(data.adSelList);
                    } else if (data.adSel && data.adSel.anaModCod) {
                        // Retrocompatibilità
                        setAdSelList([data.adSel]);
                    }
                }
            } catch (error) {
                console.error("Errore caricamento dati:", error);
            }
        };
        loadData();
    }, [labProps?.idLab]);

    // Persisti adSelList sul backend usando il nuovo formato
    const persistAdSelList = useCallback(async (newAdSelList) => {
        const payload = { 
            adSelList: newAdSelList, 
            idLab: labProps.idLab 
        };
        const formBody = ["payload=" + JSON.stringify(payload)];
        const response = await fetch(process.env.REACT_APP_URL_REPLACE_ADSEL, {
            credentials: 'include',
            method: "POST",
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: formBody
        });
        if (!response.ok) {
            throw new Error('Errore nel salvataggio delle attività didattiche');
        }
        // Aggiorna i dati in memoria nel componente padre
        if (props.setSelectedLabProps) {
            props.setSelectedLabProps(prev => ({
                ...prev,
                adSelList: newAdSelList
            }));
        }
        return response;
    }, [labProps?.idLab, props]);

    // Pulisci eventi Easy dal backend
    const clearEasyEventsOnBackend = useCallback(async () => {
        const getResponse = await fetch(process.env.REACT_APP_URL_GET_USAGE_DATA + '?idLab=' + labProps.idLab, {
            credentials: 'include', method: "GET"
        });
        if (!getResponse.ok) return;
        const currentData = await getResponse.json();
        const manualEvents = (currentData.schedulerLab || []).filter(evt => evt.scheduleType !== 'easy');

        const bookPayload = {
            idLab: labProps.idLab,
            email: labProps.email,
            baseImage: {
                os: labProps.imageOs, name: labProps.imageName, des: labProps.imageDes,
                size: labProps.imageSize, feed: labProps.imageFeed, feedBundle: labProps.imageFeedBundle,
                imageType: labProps.imageType, destination: labProps.destination,
                hasAppVolAgent: !!labProps.hasAppVolAgent,
            },
            usageData: { maxStudentsPerPartition: 0, schedulerLab: manualEvents }
        };
        const formBody = ["payload=" + JSON.stringify(bookPayload)];
        await fetch(process.env.REACT_APP_URL_BOOK_LAB, {
            credentials: 'include', method: "POST",
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: formBody
        });
        setLabEvents(manualEvents);
    }, [labProps]);

    // Aggiungi una nuova AD alla lista (evita duplicati per anaModCod + docPostaElettronica)
    const handleAddAd = useCallback(async (newAd) => {
        // Verifica duplicati per combinazione codice modulo + docente
        const isDuplicate = adSelList.some(ad => ad.anaModCod === newAd.anaModCod && ad.docPostaElettronica === newAd.docPostaElettronica);
        if (isDuplicate) {
            console.log("AD già presente:", newAd.anaModCod, newAd.docPostaElettronica);
            return;
        }
        
        const updated = [...adSelList, newAd];
        
        try {
            // Prima persisti sul backend
            await persistAdSelList(updated);
            // Poi aggiorna lo stato locale
            setAdSelList(updated);
            // Mostra dialog per chiedere se importare
            setLastAddedAd(newAd);
            setShowImportDialog(true);
        } catch (error) {
            console.error("Errore nell'aggiunta dell'AD:", error);
        }
    }, [persistAdSelList, adSelList]);

    // Gestisce l'importazione dal dialog di conferma
    const handleConfirmImport = async () => {
        setIsImporting(true);
        try {
            if (importRef.current) {
                await importRef.current.doImport();
            }
            setShowImportDialog(false);
            setLastAddedAd(null);
        } catch (error) {
            console.error("Errore nell'importazione:", error);
        } finally {
            setIsImporting(false);
        }
    };

    const handleSkipImport = () => {
        setShowImportDialog(false);
        setLastAddedAd(null);
    };

    // Rimuovi una singola AD dalla lista
    const handleRemoveAd = async (index) => {
        setIsDisassociating(true);
        try {
            const updated = adSelList.filter((_, i) => i !== index);
            await persistAdSelList(updated);
            await clearEasyEventsOnBackend();
            setAdSelList(updated);
            setShowDisassociaDialog(false);
            setDisassociaTarget(null);
        } catch (error) {
            console.error("Errore nella rimozione AD:", error);
        } finally {
            setIsDisassociating(false);
        }
    };

    // Rimuovi tutte le AD
    const handleDisassociaTutte = async () => {
        setIsDisassociating(true);
        try {
            await persistAdSelList([]);
            await clearEasyEventsOnBackend();
            setAdSelList([]);
            setShowDisassociaDialog(false);
            setDisassociaTarget(null);
        } catch (error) {
            console.error("Errore nella disassociazione:", error);
        } finally {
            setIsDisassociating(false);
        }
    };

    const openDisassociaDialog = (target) => {
        setDisassociaTarget(target);
        setShowDisassociaDialog(true);
    };

    // Rimuovi un codice Easy per indice (per gestire duplicati)
    const handleRemoveEasyCodByIndex = useCallback(async (anaModCod, easyIndex) => {
        const currentList = adSelListRef.current;
        const adIndex = currentList.findIndex(ad => ad.anaModCod === anaModCod);
        if (adIndex === -1) return;
        
        const ad = currentList[adIndex];
        if (!ad.easyAd || ad.easyAd.length === 0) return;

        const removedEasyCod = ad.easyAd[easyIndex]?.easyAdCod;
        const updatedEasyAd = ad.easyAd.filter((_, i) => i !== easyIndex);
        const updatedAd = { ...ad, easyAd: updatedEasyAd };
        const updatedList = currentList.map((item, i) => i === adIndex ? updatedAd : item);
        
        // Aggiorna subito ref e stato per feedback immediato
        adSelListRef.current = updatedList;
        setAdSelList(updatedList);
        
        try {
            await persistAdSelList(updatedList);
            // Rimuovi eventi Easy associati a questo codice (solo se non ci sono altri duplicati)
            const stillHasCode = updatedEasyAd.some(ea => ea.easyAdCod === removedEasyCod);
            if (!stillHasCode && removedEasyCod) {
                const getResponse = await fetch(process.env.REACT_APP_URL_GET_USAGE_DATA + '?idLab=' + labProps.idLab, {
                    credentials: 'include', method: "GET"
                });
                if (getResponse.ok) {
                    const currentData = await getResponse.json();
                    const filteredEvents = (currentData.schedulerLab || []).filter(
                        evt => !(evt.scheduleType === 'easy' && evt.easyAdCod === removedEasyCod)
                    );
                    const bookPayload = {
                        idLab: labProps.idLab,
                        email: labProps.email,
                        baseImage: {
                            os: labProps.imageOs, name: labProps.imageName, des: labProps.imageDes,
                            size: labProps.imageSize, feed: labProps.imageFeed, feedBundle: labProps.imageFeedBundle,
                            imageType: labProps.imageType, destination: labProps.destination,
                            hasAppVolAgent: !!labProps.hasAppVolAgent,
                        },
                        usageData: { maxStudentsPerPartition: 0, schedulerLab: filteredEvents }
                    };
                    const formBody = ["payload=" + JSON.stringify(bookPayload)];
                    await fetch(process.env.REACT_APP_URL_BOOK_LAB, {
                        credentials: 'include', method: "POST",
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                        body: formBody
                    });
                    setLabEvents(filteredEvents);
                }
            }
        } catch (error) {
            console.error("Errore nella rimozione del codice Easy:", error);
        }
    }, [labProps, persistAdSelList]);

    const onSubmit = async (e) => {
        e.preventDefault();
        var formBody = [];

        if (isSubmitting) return;
        setIsSubmitting(true);
        setIsLoading(1);

        try {
            const payload = {
                idLab: labProps.idLab,
                email: labProps.email,
                baseImage: {
                    os: labProps.imageOs, name: labProps.imageName, des: labProps.imageDes,
                    size: labProps.imageSize, feed: labProps.imageFeed, feedBundle: labProps.imageFeedBundle,
                    imageType: labProps.imageType, destination: labProps.destination,
                    hasAppVolAgent: !!labProps.hasAppVolAgent,
                },
                usageData: { maxStudentsPerPartition: 0, schedulerLab: labEvents }
            };

            formBody.push("payload=" + JSON.stringify(payload));

            const response = await fetch(process.env.REACT_APP_URL_BOOK_LAB, {
                credentials: 'include', method: "POST",
                headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                body: formBody
            });

            const data = await response.json();
            setIsSubmitted(1);
            setIsLoading(0);
            setStatusRequest(data.statusRequest);

            // Aggiorna i dati in memoria nel componente padre
            if (props.setSelectedLabProps && data.statusRequest === 'success') {
                props.setSelectedLabProps(prev => ({
                    ...prev,
                    adSelList: adSelList,
                    usageData: {
                        ...prev.usageData,
                        schedulerLab: labEvents
                    }
                }));
            }
        } catch (error) {
            console.error("Errore nel salvataggio:", error);
        } finally {
            setTimeout(() => setIsSubmitting(false), 500);
        }
    };

    if (!labProps) {
        return (
            <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: '#fff' }}>
                <Toolbar>
                    <IconButton edge="start" onClick={() => props.setIsShowScheduleLab(false)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: themeColor }}>
                        Pianificazione Laboratorio Virtuale
                    </Typography>
                </Toolbar>
            </AppBar>
        );
    }

    // Testo per il dialog di conferma disassociazione
    const disassociaDialogContent = disassociaTarget === null ? {
        title: 'Disassocia tutte le attività',
        text: 'Confermi di voler disassociare tutte le attività didattiche da questo laboratorio?',
        onConfirm: handleDisassociaTutte,
    } : {
        title: 'Disassocia attività',
        text: `Confermi di voler disassociare "${adSelList[disassociaTarget]?.anaAfDescIta}" da questo laboratorio?`,
        onConfirm: () => handleRemoveAd(disassociaTarget),
    };

    // Renderizza i codici Easy come Chip rimovibili
    const renderEasyCodes = (ad) => {
        if (!ad.easyAd || ad.easyAd.length === 0) return '—';

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {ad.easyAd.map((ea, idx) => (
                    <Tooltip 
                        key={`${ad.anaModCod}-${ea.easyAdCod}-${idx}`} 
                        title={ea.easyAdNome || ea.easyAdCod} 
                        arrow
                        disableInteractive
                        enterDelay={300}
                        leaveDelay={0}
                    >
                        <Chip
                            label={ea.easyAdCod}
                            size="small"
                            onDelete={() => handleRemoveEasyCodByIndex(ad.anaModCod, idx)}
                            sx={{
                                backgroundColor: '#ffebee',
                                '& .MuiChip-deleteIcon': { color: '#d32f2f' }
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
        );
    };

    return (
        <React.Fragment>
            <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: '#fff' }}>
                <Toolbar sx={{ gap: 2 }}>
                    <IconButton edge="start" onClick={() => props.setIsShowScheduleLab(false)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: themeColor }}>
                            Pianificazione Laboratorio Virtuale
                        </Typography>
                        <Typography variant="body2" color="textSecondary" noWrap>
                            {labProps.labName} &mdash; {labProps.baseImage.name} ({labProps.baseImage.size})
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3 }}>
                <form onSubmit={onSubmit}>

                    {/* Sezione Attività Didattiche */}
                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <Box sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Attività didattiche associate ({adSelList.length})
                            </Typography>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    Gli eventi sincronizzati da EasyRoom sono visualizzati <strong style={{ color: '#d32f2f' }}>in rosso</strong> nel calendario. 
                                    Possono essere modificati manualmente, ma verranno sovrascritti alla prossima sincronizzazione con EasyRoom.
                                    Se necessario, disassociare l'attività didattica e inserire gli eventi manualmente.
                                </Typography>
                            </Alert>
                            {hasAdSel ? (
                                <>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>Attività didattica</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Modulo</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Docente</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Codice Easy</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {adSelList.map((ad, index) => (
                                                    <TableRow key={`${ad.anaModCod}-${index}`} hover>
                                                        <TableCell>{ad.anaAfDescIta}</TableCell>
                                                        <TableCell>{ad.anaModDescIta || '—'}</TableCell>
                                                        <TableCell>{ad.docPostaElettronica || '—'}</TableCell>
                                                        <TableCell>{renderEasyCodes(ad)}</TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => openDisassociaDialog(index)}
                                                                title="Rimuovi attività"
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Divider sx={{ my: 2 }} />

                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center">
                                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                            <LabConsolleModifyAF
                                                setAdSel={handleAddAd}
                                                labProps={labProps}
                                                buttonLabel="Aggiungi attività"
                                                dialogTitle="Aggiungi attività didattica al laboratorio"
                                            />
                                            {adSelList.length > 1 && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<DeleteSweepIcon />}
                                                    onClick={() => openDisassociaDialog(null)}
                                                >
                                                    Disassocia tutte
                                                </Button>
                                            )}
                                        </Box>
                                        <LabConsolleImportFromEasy
                                            ref={importRef}
                                            buttonDescription={"Sincronizza da EasyRoom"}
                                            labProps={labProps}
                                            adSelList={adSelList}
                                            setLabEvents={setLabEvents}
                                            setIsSubmitted={setIsSubmitted}
                                            setIsSubmitble={setIsSubmitble}
                                            setStatusRequest={setStatusRequest}
                                        />
                                    </Stack>
                                </>
                            ) : (
                                <>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        Nessuna attività didattica associata a questo laboratorio.
                                        Associare un'attività per importare le pianificazioni da EasyRoom.
                                    </Alert>
                                    <LabConsolleModifyAF
                                        setAdSel={handleAddAd}
                                        labProps={labProps}
                                        buttonLabel="Associa attività didattica"
                                        dialogTitle="Associa attività didattica al laboratorio"
                                    />
                                </>
                            )}
                        </Box>
                    </Card>

                    {/* Dialog conferma disassociazione */}
                    <Dialog
                        open={showDisassociaDialog}
                        onClose={() => setShowDisassociaDialog(false)}
                        maxWidth="sm"
                    >
                        <DialogTitle sx={{ fontWeight: 600, color: themeColor }}>
                            {disassociaDialogContent.title}
                        </DialogTitle>
                        <DialogContent>
                            <Typography>{disassociaDialogContent.text}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Gli eventi Easy associati verranno rimossi dal calendario.
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button
                                onClick={() => setShowDisassociaDialog(false)}
                                disabled={isDisassociating}
                            >
                                Annulla
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={disassociaDialogContent.onConfirm}
                                disabled={isDisassociating}
                                startIcon={isDisassociating ? <ClipLoader size={16} color="#fff" /> : <DeleteOutlineIcon />}
                            >
                                Disassocia
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Dialog conferma importazione dopo aggiunta AD */}
                    <Dialog
                        open={showImportDialog}
                        onClose={handleSkipImport}
                        maxWidth="sm"
                    >
                        <DialogTitle sx={{ fontWeight: 600, color: themeColor }}>
                            Attività aggiunta con successo
                        </DialogTitle>
                        <DialogContent>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <strong>{lastAddedAd?.anaAfDescIta}</strong> è stata associata al laboratorio.
                            </Alert>
                            <Typography>
                                Vuoi importare subito le pianificazioni da EasyRoom per questa attività?
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Gli orari delle lezioni verranno aggiunti automaticamente al calendario.
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button
                                onClick={handleSkipImport}
                                disabled={isImporting}
                            >
                                Più tardi
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmImport}
                                disabled={isImporting}
                                startIcon={isImporting ? <ClipLoader size={16} color="#fff" /> : <SyncIcon />}
                            >
                                Importa ora
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Calendario */}
                    <Box sx={{ mt: 3 }}>
                        <SchedulerLab
                            email={props.apiAuthResponse}
                            baseImage={labProps}
                            labEvents={labEvents}
                            setLabEvents={setLabEvents}
                            setIsSubmitble={setIsSubmitble}
                            isSubmitted={isSubmitted}
                            defaultView={'week'}
                            defaultDate={new Date()}
                        />
                    </Box>

                    {/* Conferma e salva */}
                    <Box sx={{ mt: 3 }}>
                        <ReqLabConfirm
                            isSubmitted={isSubmitted}
                            isSubmitble={isSubmitble}
                            isSubmitting={isSubmitting}
                            statusRequest={statusRequest}
                            apiAuthGroups={props.apiAuthGroups}
                            buttonDescription={"Salva pianificazione"}
                            isAdmin={isAdmin}
                        />
                        <ClipLoader
                            size={35}
                            color={themeColor}
                            loading={isLoading}
                        />
                    </Box>

                </form>
            </Box>
        </React.Fragment>
    );
}

export default LabConsolleScheduleDettPage;
